'use client';
import * as Sentry from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { CACHE_5M } from '@cineamo/api/constants/caching-constants';

import { roomAmbienceKeys } from '@cineamo/api/keys/query-keys';
import { getCinemaRoomAmbience } from '@cineamo/api/resources/room-ambience/room-ambience-api';

import { dayKeyOfLocalDate } from '@cineamo/ui/src/helpers/cinema-time-zone';

import { ambienceModelOrigins } from '@cineamo/ui/src/room-3d/ambience/ambience';

import { IS_ANALYTICS_ENABLED } from '@cineamo/composables/src/providers/analytics-environment';
import {
    ambienceFailureReason,
    AMBIENCE_REPORT_SAMPLE,
    createSampleGate,
    decidedKillState as toDecidedKillState,
    initialKillState,
    isAmbienceLate,
    isLookupOpen,
    KILL_SWITCH_WAIT_MS,
    type KillState,
    reduceKillState,
    type ResolvedAmbience,
    ROOM_AMBIENCE_KILL_SWITCH_FLAG,
    toResolvedAmbience
} from '@cineamo/composables/src/ticket-booking/room-ambience';

/** Reports already sent this page session: one bad active document must cost one event, not one per guest and remount. */
const reportedKeys = new Set<string>();

/** Whether this page session reports ambience failures to Sentry at all. */
const isReportSampled = createSampleGate(AMBIENCE_REPORT_SAMPLE);

/** True the first time `key` is seen this page session. */
function isFirstReport(key: string): boolean {
    if (reportedKeys.has(key)) return false;
    reportedKeys.add(key);
    return true;
}

/** The last answer of the flag, kept for the page session: the seat picker remounts between booking steps. */
let decidedKillState: Exclude<KillState, 'pending'> | null = null;

/**
 * The client-side kill switch: PostHog's `room-ambience-kill-switch` flag, read only while `isActive` (so
 * only visitors who reach a 3D map are exposed to it). Where analytics is off there is no PostHog and nothing
 * to wait for; the server's `ROOM_AMBIENCE_ENABLED` covers those sites. Flags that never arrive count as clear
 * after {@link KILL_SWITCH_WAIT_MS}, so a blocked SDK costs the ambience nothing and the booking nothing.
 */
function useKillSwitch(isActive: boolean): KillState {
    const [state, setState] = useState<KillState>(() => initialKillState(IS_ANALYTICS_ENABLED, decidedKillState));

    useEffect(() => {
        if (!isActive || !IS_ANALYTICS_ENABLED) return;
        let isUnmounted = false;
        let unsubscribe: (() => void) | undefined;
        const timer = setTimeout(
            () =>
                setState((current) => {
                    const next = reduceKillState(current, { type: 'timeout' });
                    // Remembered like a flags answer: blocked flags must not make every remount wait again, nor let a
                    // cached ambience swap in after a seat pick. A later flags answer still overwrites it.
                    decidedKillState = toDecidedKillState(next);
                    return next;
                }),
            KILL_SWITCH_WAIT_MS
        );

        // Imported here, as in useSeatMapViewMode, so the SDK stays out of the bundle of apps without a PostHog key.
        import('posthog-js')
            .then(({ default: posthog }) => {
                if (isUnmounted) return;
                // Fires at once when the flags are loaded and again on every reload, so a switch thrown mid-visit lands.
                unsubscribe = posthog.onFeatureFlags(() => {
                    // Reading a flag records an exposure; this one is not an experiment.
                    const isKilled =
                        posthog.getFeatureFlag(ROOM_AMBIENCE_KILL_SWITCH_FLAG, { send_event: false }) === true;
                    setState((current) => {
                        const next = reduceKillState(current, { type: 'flags', isKilled });
                        decidedKillState = toDecidedKillState(next);
                        return next;
                    });
                });
            })
            .catch(() => {
                // The SDK chunk did not load: not killed, the timer above lets the ambience through.
            });

        return () => {
            isUnmounted = true;
            clearTimeout(timer);
            unsubscribe?.();
        };
    }, [isActive]);

    return state;
}

export type RoomAmbienceLookup = {
    cinemaId: number | undefined;
    /** The showing's date at the cinema, `YYYY-MM-DD`. */
    date: string | undefined;
    /** The Cineamo movie id of the showing's film. */
    movieId: number | undefined;
    /** An ambience slug to force (the public resolve answers only for an active, non-film one). */
    forcedSlug?: string | null;
    /** Let the server default the date to today at the cinema; otherwise the lookup waits until `date` is known. */
    isDateOptional?: boolean;
    /** Every gate the caller owns is open: opted in, 3D chosen, the room has a design. */
    isEnabled: boolean;
    /** When the guest first picked a seat: an answer first visible after that waits for the next mount. */
    interactedAt?: number | null;
};

export type { ResolvedAmbience };

/** Where an ambience failure came from: enough to find the row an admin would deactivate. */
export type AmbienceErrorContext = {
    ambienceId?: string;
    sourceSlugs?: string[];
    cinemaId?: number;
    showingId?: number;
};

/**
 * The ambience the server picks for this showing (its film's, else the season's), normalized for rendering.
 * `null` while loading, while any gate is closed, and on every failure: the booking never waits for it and never
 * breaks on it. Not suspending, retried once, cached for five minutes (the server caches it for one).
 */
export function useRoomAmbience({
    cinemaId,
    date,
    movieId,
    forcedSlug = null,
    isDateOptional = false,
    isEnabled,
    interactedAt = null
}: RoomAmbienceLookup): ResolvedAmbience | null {
    const killState = useKillSwitch(isEnabled);
    const isQueryEnabled = isLookupOpen({
        isEnabled,
        killState,
        cinemaId,
        date,
        isDateOptional,
        today: dayKeyOfLocalDate(new Date())
    });
    // The late-answer clock starts when the lookup does (3D chosen, flags answered), not when the seat picker mounts:
    // a guest who switches 2D to 3D after a while still gets the ambience.
    const [enabledAt, setEnabledAt] = useState<number | null>(null);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- stamps the moment the lookup opened and clears it on close
        setEnabledAt(isQueryEnabled ? Date.now() : null);
    }, [isQueryEnabled]);

    const queryKey = roomAmbienceKeys.resolve(cinemaId ?? 0, date ?? null, movieId ?? null, forcedSlug);
    const { data, dataUpdatedAt } = useQuery({
        queryKey,
        queryFn: async ({ signal }): Promise<ResolvedAmbience | null> => {
            const response = await getCinemaRoomAmbience(
                cinemaId as number,
                { date, movieId, ...(forcedSlug ? { ambience: forcedSlug } : {}) },
                signal
            );
            // A failed request resolves null; thrown, it is retried once and then settles as "no ambience".
            if (!response) throw new Error('Room ambience could not be loaded');
            const modelOrigins = ambienceModelOrigins(process.env.NEXT_PUBLIC_ENV_CDN_HOST_NAME);
            const { resolved, warnings, sourceSlugs, sourceIds } = toResolvedAmbience(response, modelOrigins);
            if (
                warnings.length > 0 &&
                isReportSampled() &&
                isFirstReport(`${sourceSlugs.join('+')}:${warnings.join('|')}`)
            ) {
                Sentry.captureMessage('Room ambience normalized with warnings', {
                    level: 'warning',
                    tags: {
                        room_ambience: 'normalize',
                        room_ambience_sample: String(AMBIENCE_REPORT_SAMPLE),
                        room_ambience_id: resolved?.ambience.id ?? sourceSlugs.join('+'),
                        room_ambience_sources: sourceSlugs.join('+')
                    },
                    extra: { warnings, sourceSlugs, sourceIds, cinemaId, date, movieId }
                });
            }
            return resolved;
        },
        enabled: isQueryEnabled,
        staleTime: CACHE_5M,
        // apps/web defaults to refetchOnMount:false and a 24 h gcTime: without these an admin's switch-off (row,
        // cinema opt-in, server kill switch) would never reach a tab that already holds the answer.
        gcTime: CACHE_5M,
        refetchOnMount: true,
        retry: 1,
        // The answer is judged by when it first became visible this mount, see isAmbienceLate.
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    });

    // Gates closing after the data arrived (3D left, kill switch thrown) drop the ambience without a refetch.
    // An answer first visible late (slow API) or after the first seat pick would compile shaders and change the room
    // under the guest's finger: it stays cached and shows from the next mount, whose clock starts over. First sight
    // is recorded per lookup, stamped with that answer's arrival: cached data predates the mount, and a later
    // refetch never takes a shown ambience away.
    const [firstSeen, setFirstSeen] = useState<{ key: string; at: number } | null>(null);
    const key = JSON.stringify(queryKey);
    const isCandidate = isQueryEnabled && !!data;
    // Adjusting state while rendering: React re-renders at once, before anything is committed.
    if (!isCandidate) {
        if (firstSeen?.key === key) setFirstSeen(null);
    } else if (firstSeen?.key !== key) {
        setFirstSeen({ key, at: dataUpdatedAt });
    }
    // Until the effect above has stamped the clock, nothing can be late yet.
    const isLate =
        enabledAt !== null &&
        isAmbienceLate({ firstSeenAt: firstSeen?.key === key ? firstSeen.at : null, enabledAt, interactedAt });
    return isQueryEnabled && data && !isLate ? data : null;
}

/**
 * A runtime failure inside the ambience layer: reported once per page session at warning level, the plain room
 * carries on. A model that merely loaded too slowly is network weather, not a defect: a breadcrumb only.
 */
export function reportAmbienceError(error: unknown, layerId: string, context: AmbienceErrorContext = {}): void {
    const message = error instanceof Error ? error.message : String(error);
    if (!isReportSampled() || !isFirstReport(`${context.ambienceId ?? ''}:${layerId}:${message}`)) return;
    if (ambienceFailureReason(error) === 'model_timeout') {
        Sentry.addBreadcrumb({ category: 'room_ambience', level: 'warning', message });
        return;
    }
    Sentry.captureException(error, {
        level: 'warning',
        tags: {
            room_ambience: layerId,
            room_ambience_sample: String(AMBIENCE_REPORT_SAMPLE),
            room_ambience_layer: layerId,
            ...(context.ambienceId ? { room_ambience_id: context.ambienceId } : {})
        },
        extra: { sourceSlugs: context.sourceSlugs, cinemaId: context.cinemaId, showingId: context.showingId }
    });
}
